import { Clock, Play } from "lucide-react";

/* ---------------- CONSTANTS ---------------- */

const DEFAULT_THUMBNAIL =
  "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?q=80&w=1200";

const DEFAULT_TITLE = "Untitled Video";

/* ---------------- COLOR LOGIC ---------------- */

const getProgressColor = (progress) => {
  if (progress < 30) {
    return {
      text: "text-blue-400",
      border: "border-blue-500/40",
      bg: "bg-blue-500/20",
    };
  } else if (progress < 70) {
    return {
      text: "text-yellow-400",
      border: "border-yellow-500/40",
      bg: "bg-yellow-500/20",
    };
  } else {
    return {
      text: "text-orange-400",
      border: "border-orange-500/40",
      bg: "bg-orange-500/20",
    };
  }
};

/* ---------------- COMPONENT ---------------- */

export default function JobCard({ job, isActive, onClick }) {
  const progress = job.progress || 8;

  const colors = getProgressColor(progress);

  /* ---------------- SAFE VALUES ---------------- */

  const thumbnail =
    job.thumbnail_url && job.thumbnail_url !== ""
      ? job.thumbnail_url
      : DEFAULT_THUMBNAIL;

  const title = job.title && job.title !== "" ? job.title : DEFAULT_TITLE;

  return (
    <div
      onClick={() => onClick(job)}
      className={`group cursor-pointer transition-all duration-300 ${
        isActive ? "scale-[0.99]" : "hover:-translate-y-[2px]"
      }`}
    >
      {/* THUMBNAIL */}
      <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-black">
        <img
          src={thumbnail}
          onError={(e) => {
            e.target.src = DEFAULT_THUMBNAIL;
          }}
          className="w-full h-40 object-cover opacity-90 group-hover:opacity-100 transition"
        />

        {/* DARK OVERLAY */}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />

        {/* CTA BUTTON (only when done) */}
        {job.status === "done" && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            <button className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-full shadow-lg hover:scale-105 transition">
              View Clips
            </button>
          </div>
        )}

        {/* PLATFORM BADGE */}
        <div className="absolute bottom-2 left-2 text-xs bg-black/70 text-white px-2 py-0.5 rounded">
          {job.source_url ? "YouTube" : "Uploaded"}
        </div>

        {/* PLAN BADGE */}
        <div className="absolute top-3 right-3 text-xs bg-black/70 text-white px-3 py-1 rounded-full backdrop-blur-sm">
          Free Plan
        </div>

        {/* 🔥 PROGRESS UI */}
        {job.status !== "done" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`relative flex items-center gap-3 px-4 py-2 rounded-xl backdrop-blur-md overflow-hidden border ${colors.border} bg-black/80 shadow-lg`}
            >
              {/* Progress Fill */}
              <div
                className={`absolute inset-0 ${colors.bg} transition-all duration-700 ease-out`}
                style={{ width: `${progress}%` }}
              />

              {/* Shimmer Effect */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="w-1/3 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              </div>

              {/* Content */}
              <div
                className={`relative flex items-center gap-3 z-10 ${colors.text}`}
              >
                <Clock className="w-4 h-4 animate-pulse" />

                <span className="text-sm font-medium">
                  {progress}% (ETA{" "}
                  {Math.max(1, Math.round((100 - progress) / 10))}m)
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TEXT */}
      <div className="mt-3 px-1">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {title}
          </h3>

          <button className="text-gray-400 hover:text-gray-700 text-lg">
            ⋯
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-1">
          {job.status === "done" ? "Ready" : "Processing"}
        </p>
      </div>
    </div>
  );
}
